import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay } from 'rxjs/operators';
import { BE_API_URL, TRANSLATION_ENABLED } from '../constants/app.constants';

const BACKEND_SOURCE_LANG = 'de';

export interface LandingTranslationContext {
  companyId: string;
  propertyId: string;
}

interface TranslationItemRequest {
  id: string;
  text: string;
}

interface TranslationItemResponse {
  id: string;
  translatedText: string;
}

interface TranslationResponse {
  items: TranslationItemResponse[];
}

interface QueuedTranslation {
  id: string;
  text: string;
  subscriber: Subscriber<string>;
}

interface TranslationQueue {
  context: LandingTranslationContext;
  sourceLanguage: string;
  targetLanguage: string;
  items: QueuedTranslation[];
}

interface ActiveTranslationBatch {
  items: QueuedTranslation[];
  subscription: Subscription;
}

const MAX_BATCH_ITEMS = 25;
const MAX_BATCH_CHARACTERS = 10_000;

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private landingContext: LandingTranslationContext | null = null;
  private readonly inflight = new Map<string, Observable<{ responseData: { translatedText: string } }>>();
  private readonly cache = new Map<string, { responseData: { translatedText: string } }>();
  private readonly translationQueues = new Map<string, TranslationQueue>();
  private readonly activeTranslationBatches = new Set<ActiveTranslationBatch>();
  private flushScheduled = false;
  private requestSequence = 0;
  private requestGeneration = 0;
  private readonly translationEnabled = TRANSLATION_ENABLED;

  constructor(private readonly http: HttpClient) {}

  setLandingContext(context: LandingTranslationContext | null): void {
    this.landingContext = context;
    this.resetForLanguageChange();
  }

  resetForLanguageChange(): void {
    this.requestGeneration += 1;
    for (const queue of this.translationQueues.values()) {
      for (const item of queue.items) {
        if (!item.subscriber.closed) {
          item.subscriber.error(new Error('Translation request cancelled'));
        }
      }
    }
    this.translationQueues.clear();
    for (const batch of this.activeTranslationBatches) {
      for (const item of batch.items) {
        if (!item.subscriber.closed) {
          item.subscriber.error(new Error('Translation request cancelled'));
        }
      }
      batch.subscription.unsubscribe();
    }
    this.activeTranslationBatches.clear();
    this.flushScheduled = false;
    this.inflight.clear();
    this.cache.clear();
  }

  translateText(
    text: string,
    targetLang: string,
    sourceLang: string = BACKEND_SOURCE_LANG
  ): Observable<{ responseData: { translatedText: string } }> {
    const source = (sourceLang || BACKEND_SOURCE_LANG).trim().toLowerCase();
    const target = (targetLang || '').trim().toLowerCase();
    const fallback = { responseData: { translatedText: text } };

    if (!this.translationEnabled || !text || !target || target === source || !this.landingContext) {
      return of(fallback);
    }

    const key = `${source}__${target}__${text}`;
    const cached = this.cache.get(key);
    if (cached) return of(cached);

    const pending = this.inflight.get(key);
    if (pending) return pending;

    const requestGeneration = this.requestGeneration;
    const request$ = this.queueTranslation(text, source, target).pipe(
      mergeMap((translatedText) => {
        if (requestGeneration !== this.requestGeneration) {
          return throwError(() => new Error('Translation request cancelled'));
        }
        const result = { responseData: { translatedText: translatedText || text } };
        this.cache.set(key, result);
        return of(result);
      }),
      // Provider failures deliberately remain uncached so a later request can retry.
      catchError(() => of(fallback)),
      finalize(() => this.inflight.delete(key)),
      shareReplay(1)
    );

    this.inflight.set(key, request$);
    return request$;
  }

  translateBatch(
    items: TranslationItemRequest[],
    sourceLanguage: string,
    targetLanguage: string
  ): Observable<Map<string, string>> {
    const context = this.landingContext;
    const source = (sourceLanguage || BACKEND_SOURCE_LANG).trim().toLowerCase();
    const target = (targetLanguage || '').trim().toLowerCase();
    const originals = new Map(items.map((item) => [item.id, item.text]));

    if (!this.translationEnabled || !context || source === target || !items.length) {
      return of(originals);
    }

    return this.requestBatch(context, items, source, target);
  }

  private queueTranslation(text: string, sourceLanguage: string, targetLanguage: string): Observable<string> {
    const context = this.landingContext;
    if (!context) {
      return of(text);
    }

    return new Observable<string>((subscriber) => {
      const queueKey = [context.companyId, context.propertyId, sourceLanguage, targetLanguage].join('__');
      const queue = this.translationQueues.get(queueKey) || {
        context: { ...context },
        sourceLanguage,
        targetLanguage,
        items: [],
      };
      this.translationQueues.set(queueKey, queue);
      queue.items.push({ id: `translation-${this.requestSequence++}`, text, subscriber });
      this.scheduleQueueFlush();
    });
  }

  private scheduleQueueFlush(): void {
    if (this.flushScheduled) {
      return;
    }
    this.flushScheduled = true;
    queueMicrotask(() => {
      this.flushScheduled = false;
      this.flushTranslationQueues();
    });
  }

  private flushTranslationQueues(): void {
    const queues = [...this.translationQueues.values()];
    this.translationQueues.clear();

    for (const queue of queues) {
      const activeItems = queue.items.filter((item) => !item.subscriber.closed);
      for (const chunk of this.chunkQueueItems(activeItems)) {
        const batch: ActiveTranslationBatch = {
          items: chunk,
          subscription: new Subscription(),
        };
        batch.subscription = this.requestBatch(
          queue.context,
          chunk.map(({ id, text }) => ({ id, text })),
          queue.sourceLanguage,
          queue.targetLanguage
        ).subscribe({
          next: (translated) => {
            for (const item of chunk) {
              if (!item.subscriber.closed) {
                const translatedText = translated.get(item.id);
                if (typeof translatedText === 'string' && translatedText.trim()) {
                  item.subscriber.next(translatedText);
                  item.subscriber.complete();
                } else {
                  item.subscriber.error(new Error('Translation response did not include this item'));
                }
              }
            }
          },
          error: (error) => {
            for (const item of chunk) {
              if (!item.subscriber.closed) {
                item.subscriber.error(error);
              }
            }
          },
        });
        this.activeTranslationBatches.add(batch);
        batch.subscription.add(() => this.activeTranslationBatches.delete(batch));
      }
    }
  }

  private chunkQueueItems(items: QueuedTranslation[]): QueuedTranslation[][] {
    const chunks: QueuedTranslation[][] = [];
    let chunk: QueuedTranslation[] = [];
    let characterCount = 0;

    for (const item of items) {
      const itemCharacters = item.text.length;
      if (chunk.length && (chunk.length >= MAX_BATCH_ITEMS || characterCount + itemCharacters > MAX_BATCH_CHARACTERS)) {
        chunks.push(chunk);
        chunk = [];
        characterCount = 0;
      }
      chunk.push(item);
      characterCount += itemCharacters;
    }
    if (chunk.length) {
      chunks.push(chunk);
    }
    return chunks;
  }

  private requestBatch(
    context: LandingTranslationContext,
    items: TranslationItemRequest[],
    source: string,
    target: string
  ): Observable<Map<string, string>> {
    return this.http.post<TranslationResponse>(
      `${BE_API_URL}/properties/${encodeURIComponent(context.companyId)}/${encodeURIComponent(context.propertyId)}/translations`,
      { sourceLanguage: source, targetLanguage: target, items }
    ).pipe(
      map((response) => {
        if (!Array.isArray(response?.items)) {
          throw new Error('Malformed translation response');
        }
        const expectedIds = new Set(items.map((item) => item.id));
        const translated = new Map<string, string>();
        for (const item of response?.items || []) {
          if (
            typeof item?.id === 'string'
            && expectedIds.has(item.id)
            && typeof item?.translatedText === 'string'
            && item.translatedText.trim()
          ) {
            translated.set(item.id, item.translatedText.trim());
          }
        }
        return translated;
      })
    );
  }
}
